import type { U3141ChildLotChain, U3141NodePositions } from '../U3141Service';
import { u3141CollectLotChainGraphEdgePairs } from './u3141BuildLotChainGraphEdges';
import {
  U3141_GRAPH_LAYOUT_CHILD_OFFSET_Y,
  U3141_GRAPH_LAYOUT_SIBLING_SPACING_X,
} from './u3141GraphLayoutSpacing';

export { U3141_GRAPH_LAYOUT_CHILD_OFFSET_Y, U3141_GRAPH_LAYOUT_SIBLING_SPACING_X } from './u3141GraphLayoutSpacing';

export interface U3141ApplyHierarchyLayoutOptions {
  /** Positions à ne jamais modifier (nœuds déjà sur le canvas). */
  preservePositions?: U3141NodePositions;
}

/**
 * Place uniquement les nœuds absents de preservePositions sous leurs holders (BL-0697).
 * Les nœuds existants conservent leurs coordonnées ; pas de dagre global.
 */
export function u3141PlaceNewGraphNodesByHierarchy(
  data: U3141ChildLotChain[],
  preservePositions: U3141NodePositions,
): U3141NodePositions {
  const positions: U3141NodePositions = { ...preservePositions };
  if (!Array.isArray(data) || data.length === 0) return positions;

  const pairs = u3141CollectLotChainGraphEdgePairs(data);

  // Positions API (ex. clic create, concordancePlacements backend)
  data.forEach((row) => {
    const id = row.id ? String(row.id) : '';
    if (!id || id in preservePositions) return;
    const np = row.nodePosition;
    if (np != null && typeof np.x === 'number' && typeof np.y === 'number') {
      positions[id] = { x: np.x, y: np.y };
    }
  });

  const isLayoutable = (id: string) => !(id in preservePositions);

  let changed = true;
  let guard = 0;
  while (changed && guard < data.length + 5) {
    guard += 1;
    changed = false;
    for (const { holderId, originId } of pairs) {
      if (!isLayoutable(originId)) continue;
      const holderPos = positions[holderId];
      if (!holderPos) continue;

      const siblingOrigins = pairs.filter((p) => p.holderId === holderId).map((p) => p.originId);
      const layoutableSiblings = siblingOrigins.filter(isLayoutable);
      const index = layoutableSiblings.indexOf(originId);
      if (index < 0) continue;

      const n = layoutableSiblings.length;
      const next = {
        x: holderPos.x + (index - (n - 1) / 2) * U3141_GRAPH_LAYOUT_SIBLING_SPACING_X,
        y: holderPos.y + U3141_GRAPH_LAYOUT_CHILD_OFFSET_Y,
      };

      const cur = positions[originId];
      if (!cur || cur.x !== next.x || cur.y !== next.y) {
        positions[originId] = next;
        changed = true;
      }
    }
  }

  return positions;
}

/** Applique le layout hiérarchique sans déplacer les nœuds déjà positionnés (BL-0697). */
export function u3141ApplyHierarchyLayoutToLotChainGraph(
  data: U3141ChildLotChain[],
  options?: U3141ApplyHierarchyLayoutOptions,
): U3141ChildLotChain[] {
  if (!Array.isArray(data) || data.length === 0) return data;
  const preserve = options?.preservePositions ?? {};
  const positions = u3141PlaceNewGraphNodesByHierarchy(data, preserve);

  return data.map((row) => {
    const id = row.id ? String(row.id) : '';
    if (!id) return row;
    if (id in preserve) {
      return { ...row, nodePosition: preserve[id] };
    }
    const pos = positions[id];
    if (pos) return { ...row, nodePosition: pos };
    return row;
  });
}

/** @deprecated Tests / outils — préférer u3141PlaceNewGraphNodesByHierarchy avec preserve. */
export function u3141ComputeHierarchyNodePositions(data: U3141ChildLotChain[]): U3141NodePositions {
  return u3141PlaceNewGraphNodesByHierarchy(data, {});
}
